import { useRef, useState } from 'react';

export interface NumberFieldProps {
  value: number;
  onChange(value: number): void;
  /** Called when a drag/typing gesture starts and ends (for undo grouping). */
  onGestureStart?(): void;
  onGestureEnd?(): void;
  step?: number;
  min?: number;
  max?: number;
  /** Display multiplier, e.g. 100 to show 0..1 as percent. */
  displayScale?: number;
  precision?: number;
  unit?: string;
  /** Extra context shown in the tooltip above the usage hint. */
  hint?: string;
}

/**
 * Compact numeric input: drag horizontally to scrub (Shift = fine, Alt = coarse), click to type,
 * arrow keys to step.
 */
export function NumberField({
  value,
  onChange,
  onGestureStart,
  onGestureEnd,
  step = 1,
  min,
  max,
  displayScale = 1,
  precision,
  unit,
  hint,
}: NumberFieldProps) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState('');
  const drag = useRef<{ x: number; start: number; moved: boolean } | null>(null);
  const displayStep = step * displayScale;
  const digits = precision ?? (displayStep >= 1 ? 0 : Math.min(3, Math.ceil(-Math.log10(displayStep))));

  const clamp = (v: number) => {
    let out = v;
    if (min !== undefined) out = Math.max(min, out);
    if (max !== undefined) out = Math.min(max, out);
    return out;
  };
  const shown = (value * displayScale).toFixed(digits);
  const tooltip = `${hint ? `${hint}\n` : ''}Drag to adjust (Shift: fine, Alt: coarse) · click to type`;

  const commitText = () => {
    setEditing(false);
    const parsed = Number.parseFloat(text);
    if (Number.isFinite(parsed)) {
      onGestureStart?.();
      onChange(clamp(parsed / displayScale));
      onGestureEnd?.();
    }
  };

  if (editing) {
    return (
      <div className="number-field">
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commitText}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitText();
            if (e.key === 'Escape') setEditing(false);
            e.stopPropagation();
          }}
        />
        {unit && <span className="unit">{unit}</span>}
      </div>
    );
  }

  return (
    <div className="number-field" data-tooltip={tooltip}>
      <div
        className="scrub"
        tabIndex={0}
        onPointerDown={(e) => {
          (e.target as HTMLElement).setPointerCapture(e.pointerId);
          drag.current = { x: e.clientX, start: value, moved: false };
        }}
        onPointerMove={(e) => {
          const d = drag.current;
          if (!d) return;
          const dx = e.clientX - d.x;
          if (!d.moved && Math.abs(dx) < 3) return;
          if (!d.moved) {
            d.moved = true;
            onGestureStart?.();
          }
          const factor = e.shiftKey ? 0.1 : e.altKey ? 10 : 1;
          onChange(clamp(d.start + dx * step * factor));
        }}
        onPointerUp={() => {
          const d = drag.current;
          drag.current = null;
          if (d?.moved) onGestureEnd?.();
          else {
            setText(shown);
            setEditing(true);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            e.stopPropagation();
            const dir = e.key === 'ArrowUp' ? 1 : -1;
            onGestureStart?.();
            onChange(clamp(value + dir * step * (e.shiftKey ? 10 : 1)));
            onGestureEnd?.();
          } else if (e.key === 'Enter') {
            setText(shown);
            setEditing(true);
          }
        }}
      >
        {shown}
      </div>
      {unit && <span className="unit">{unit}</span>}
    </div>
  );
}
